// The built-in dashboards: their names, Access-Management menu codes and web addresses.
//
// Each dashboard used to be a tab of one page at /dashboard, held in page memory. It now
// has its own address (/dashboard/<slug>) so it can be linked, bookmarked and measured.
// The KEYS and MENU CODES are unchanged: a role's access to a dashboard is still the
// `dashboard_*` child menu it holds (see server.ts ensureDashboardTabMenus).
//
// A DashTab is the KEY the code compares and stores. What a person READS is dashLabel(),
// which is the key for every dashboard but one.

export type DashTab =
  | 'Deployment' | 'Attendance' | 'Attrition' | 'Red Flag' | 'Yellow Flag' | 'Appeals'
  | 'Mood' | 'Approvals' | 'Onboarding' | 'Training' | 'Score Movement' | 'Uniform & Asset Management'
  | 'Data Hygiene' | 'Hiring' | 'EWS';

// Order is the order of the menu rows seeded in server.ts ensureDashboardTabMenus. Keep
// the two in step so Access Management and the dashboards don't disagree.
export const ALL_DASH_TABS: readonly DashTab[] = [
  'Deployment', 'Attendance', 'Attrition', 'Red Flag', 'Yellow Flag', 'Appeals',
  'Mood', 'Approvals', 'Onboarding', 'Training', 'Score Movement', 'Uniform & Asset Management',
  'Data Hygiene', 'Hiring', 'EWS',
];

export const DASH_TAB_MENU_CODE: Record<DashTab, string> = {
  Deployment: 'dashboard_deployment',
  Attendance: 'dashboard_attendance',
  Attrition: 'dashboard_attrition',
  'Red Flag': 'dashboard_red_flag',
  'Yellow Flag': 'dashboard_yellow_flag',
  Appeals: 'dashboard_appeals',
  Mood: 'dashboard_mood',
  Approvals: 'dashboard_approvals',
  // Held back from ensureDashboardTabMenus' first-boot grant, so this one reaches
  // only super admins until a role is given it: its tick is also the 'web.onboarding'
  // right (the server's page-rights map), which its API checks.
  Onboarding: 'dashboard_onboarding',
  Training: 'dashboard_training',
  'Score Movement': 'dashboard_score_movement',
  // Held back for Onboarding's reason: its tick is also the 'web.uniform_dashboard'
  // right, which its API checks for the project. A role holding the Dashboard but not
  // this tab is shown it (the fail-open below) and gets the API's 403, which the
  // dashboard renders as its own "access needed" panel.
  'Uniform & Asset Management': 'dashboard_uniform_assets',
  // Held back from the first-boot grant too, so it reaches only super admins until a
  // role is given this menu. There is no feature key behind it: its API answers only
  // someone who sees the whole project. Anyone else gets a 403 and the dashboard draws
  // its own quiet panel.
  'Data Hygiene': 'dashboard_data_hygiene',
  // Since 15 Oct 2026. Its tick is the Hiring rights (web.hiring_view, web.hiring_request,
  // web.hiring_manage: the server's page-rights map), and it is never part of the fail-open
  // below: a role reaches it only by being given this menu. The server also asks whether
  // hiring is switched on for the project, so the page shows it only where the server's
  // own access answer says yes (DashboardPage, HomePage).
  Hiring: 'dashboard_hiring',
  // Since 26 Oct 2026. Held back from the first-boot grant by the product owner's choice:
  // super admins see it first, other roles once it is ticked for them. It has no feature
  // key; its API answers with the Dashboard's own scope (whole project, or the team).
  EWS: 'dashboard_ews',
};

// The dashboards whose name on screen is not their key. Two things were called
// "Approvals": this dashboard, which only counts leave and attendance requests, and the
// page under My Approvals where they are decided. The dashboard took the new name; its
// key, menu code and address keep the old word so no saved link, grant or comparison
// changes. Keep the name in step with the menu row server.ts seeds, which is what
// Access Management prints.
const DASH_TAB_LABEL: Partial<Record<DashTab, string>> = {
  Approvals: 'Leave & Attendance Requests',
};

/**
 * A dashboard's name as a person reads it. Print this, never the key, wherever the name
 * is shown: a role holding the Dashboard but none of its per-dashboard menu rows is
 * shown all thirteen and has no row to read a name from, so the name cannot come from
 * the menu.
 */
export const dashLabel = (tab: DashTab): string => DASH_TAB_LABEL[tab] ?? tab;

export const DASH_TAB_SLUG: Record<DashTab, string> = {
  Deployment: 'deployment',
  Attendance: 'attendance',
  Attrition: 'attrition',
  'Red Flag': 'red-flag',
  'Yellow Flag': 'yellow-flag',
  Appeals: 'appeals',
  Mood: 'mood',
  Approvals: 'approvals',
  Onboarding: 'onboarding',
  Training: 'training',
  'Score Movement': 'score-movement',
  'Uniform & Asset Management': 'uniform-assets',
  'Data Hygiene': 'data-hygiene',
  Hiring: 'hiring',
  EWS: 'ews',
};

/** The home screen: what is waiting, what is worth a look, and every dashboard. */
export const DASHBOARD_HOME_PATH = '/dashboard';

/**
 * The home screen's open tab is in its address: `?tab=dashboards` opens "Dashboards"; a
 * plain address opens "Pending actions" (the default).
 */
export const HOME_TAB_PARAM = 'tab';
export const HOME_DASHBOARDS_TAB = 'dashboards';

/**
 * The home screen on its "Dashboards" tab: every dashboard the person holds and the ones the
 * team built. Where "All dashboards" and the "Dashboard" step of a dashboard's trail go.
 */
export const DASHBOARDS_TAB_PATH = `${DASHBOARD_HOME_PATH}?${HOME_TAB_PARAM}=${HOME_DASHBOARDS_TAB}`;

export const dashboardPath = (tab: DashTab): string => `${DASHBOARD_HOME_PATH}/${DASH_TAB_SLUG[tab]}`;

export const customDashboardPath = (id: number | string): string => `${DASHBOARD_HOME_PATH}/custom/${id}`;

/**
 * The address that opens a dashboard on one of its lists. The open list is the ?list
 * value and nothing else, so page search, the home screen and a colleague's link all
 * land on the same thing. A dashboard ignores a slug that is not one of its own.
 */
export const dashboardListPath = (tab: DashTab, listSlug: string): string =>
  `${dashboardPath(tab)}?list=${encodeURIComponent(listSlug)}`;

export function dashTabFromSlug(slug: string | undefined | null): DashTab | null {
  if (!slug) return null;
  const wanted = slug.trim().toLowerCase();
  return ALL_DASH_TABS.find((t) => DASH_TAB_SLUG[t] === wanted) ?? null;
}

/**
 * The dashboards a role reaches ONLY by holding their own menu row: never by the fail-open
 * below. Hiring's tick is its rights on the server. EWS starts with super admins and
 * reaches a role only once it is ticked for it (the product owner, 26 Oct 2026).
 */
const OWN_TICK_ONLY: ReadonlySet<DashTab> = new Set<DashTab>(['Hiring', 'EWS']);

/**
 * Which dashboards a person may open, from the menu codes they hold.
 *
 * Fail-open, exactly as the Dashboard page has always done: Dashboard access but NONE of
 * the per-dashboard child menus (legacy / pre-migration role) shows all of them, never a
 * blank dashboard. Super admins hold every menu, so they get all. Hiring is never given by
 * the fail-open (OWN_TICK_ONLY) - and holding it IS holding a per-dashboard row: the launch
 * migration (2026-10-16) gave the Hiring tab to every role with Stores ticked, and for a role
 * that had no Dashboard before, the menu now carries the Dashboard row as Hiring's parent.
 * Such a role sees Dashboard › Hiring and nothing else, never every dashboard. (A role that
 * held the Dashboard row itself is handed the other tabs by start-up, server.ts
 * ensureDashboardTabMenus step 2A, so it keeps what it saw.)
 */
export function allowedDashTabs(menuCodes: Iterable<string>): DashTab[] {
  const codes = menuCodes instanceof Set ? menuCodes : new Set(menuCodes);
  const held = (t: DashTab) => codes.has(DASH_TAB_MENU_CODE[t]);
  const shared = ALL_DASH_TABS.filter((t) => !OWN_TICK_ONLY.has(t));
  const holdsAny = ALL_DASH_TABS.some(held);
  return holdsAny ? ALL_DASH_TABS.filter(held) : shared;
}

// ── Deployment's six lists ────────────────────────────────────────────────────────
// The list open inside Deployment is kept in the address as ?list=<slug>, so the home
// screen (and a colleague's link) can land on, say, Undeployed Stores.
//
// Every dashboard keeps its open list in the same ?list value, with its own slugs. Those
// live in each dashboard's lists file (components/dashboard/lists); Deployment's are
// here because the page itself is drawn from them.
export const DEPLOYMENT_LISTS = [
  'Store Performance', 'Bottom Box', 'Undeployed Stores', 'MisDeployed Stores',
  'Undeployed Employees', 'Outside Approved – Redeploy',
] as const;
export type DeploymentList = (typeof DEPLOYMENT_LISTS)[number];

export const DEPLOYMENT_LIST_SLUG: Record<DeploymentList, string> = {
  'Store Performance': 'store-performance',
  'Bottom Box': 'bottom-box',
  'Undeployed Stores': 'undeployed-stores',
  'MisDeployed Stores': 'misdeployed-stores',
  'Undeployed Employees': 'undeployed-employees',
  'Outside Approved – Redeploy': 'outside-approved-redeploy',
};

export function deploymentListFromSlug(slug: string | undefined | null): DeploymentList | null {
  if (!slug) return null;
  const wanted = slug.trim().toLowerCase();
  return DEPLOYMENT_LISTS.find((l) => DEPLOYMENT_LIST_SLUG[l] === wanted) ?? null;
}

// ── The default Role filter ───────────────────────────────────────────────────────
// Every dashboard opens narrowed to the roles that actually WORK AT A STORE. The home
// screen must count the same people, or its numbers would not match the dashboard they
// open — so the rule lives here, once, for both.
//
// Two conditions, both needed:
//  • `roles.category === 'L1'` — the ladder's shop-floor tier (L2..L5 are AM / Trade
//    Head / Admin). Same test the red-flag engine uses to exclude managers.
//  • the role has at least one employee DEPLOYED to a store, per the store-intelligence
//    people lens. L1 on its own is only "not a manager tier", so it also covers
//    back-office roles that never stand in a store.
//
// Names are TRIMMED (the Role dropdown trims too). Fails OPEN: nothing matched returns
// an empty list, which means "everyone", never "nobody".
export interface ProjectRoleLite { id: string; name: string; category: string }

interface IntelligenceLike {
  stores?: Record<string, { people?: { employees?: Array<{ roleName?: string | null }> } } | undefined> | null;
}

export function storeLevelRoleNames(projectRoles: ProjectRoleLite[], intelligence: IntelligenceLike | null | undefined): string[] {
  const deployedRoles = new Set<string>();
  for (const st of Object.values(intelligence?.stores ?? {})) {
    for (const e of st?.people?.employees ?? []) {
      const name = (e.roleName || '').trim().toLowerCase();
      if (name) deployedRoles.add(name);
    }
  }
  return projectRoles
    .filter((r) => r.category.trim().toLowerCase() === 'l1' && deployedRoles.has(r.name.trim().toLowerCase()))
    .map((r) => r.name.trim());
}
